import React, { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { MessageSquarePlus, Send, X, Star } from "lucide-react";
import { toast } from "sonner";

const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim() || message.trim().length < 5) {
      toast.error("Please write at least 5 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        message: message.trim(),
        rating: rating || null,
      });
      setSubmitted(true);
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setMessage("");
        setRating(0);
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send feedback");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 text-white shadow-lg shadow-sky-500/25 hover:shadow-xl hover:shadow-sky-500/30 hover:scale-105 transition-all duration-200"
          data-testid="feedback-trigger-btn"
        >
          <MessageSquarePlus className="w-4 h-4" strokeWidth={2} />
          <span className="text-sm font-medium hidden sm:inline">Feedback</span>
        </button>
      )}

      {/* Feedback panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 animate-in slide-in-from-bottom-4 duration-200" data-testid="feedback-panel">
          <div className="glass-card border border-sky-200/50 shadow-2xl shadow-sky-500/10 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-sky-500/10 to-indigo-500/10 border-b border-sky-100">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className="w-4 h-4 text-sky-600" />
                <span className="text-sm font-medium text-sky-800">Share Your Feedback</span>
              </div>
              <button
                onClick={() => { setOpen(false); setSubmitted(false); }}
                className="text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="feedback-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              {submitted ? (
                <div className="text-center py-6" data-testid="feedback-success">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sky-800 font-medium">Thank you!</p>
                  <p className="text-sm text-slate-500 mt-1">Your feedback helps us improve LumiFit.</p>
                </div>
              ) : (
                <>
                  {/* Star rating */}
                  <div className="flex items-center gap-1 mb-3" data-testid="feedback-rating">
                    <span className="text-xs text-slate-500 mr-1">Rating:</span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star === rating ? 0 : star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="transition-transform hover:scale-110"
                        data-testid={`feedback-star-${star}`}
                      >
                        <Star
                          className={`w-5 h-5 transition-colors ${
                            star <= (hoverRating || rating)
                              ? "text-amber-400 fill-amber-400"
                              : "text-slate-300"
                          }`}
                          strokeWidth={1.5}
                        />
                      </button>
                    ))}
                  </div>

                  {/* Message */}
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What's working well? What could be better?"
                    className="w-full min-h-[100px] rounded-lg border border-sky-200 bg-white/80 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 resize-y"
                    data-testid="feedback-message-input"
                    maxLength={2000}
                  />
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-slate-400">{message.length}/2000</span>
                    <Button
                      onClick={handleSubmit}
                      disabled={loading || message.trim().length < 5}
                      className="glow-button text-sm px-4 py-2"
                      data-testid="feedback-submit-btn"
                    >
                      {loading ? (
                        <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <Send className="w-3.5 h-3.5" /> Send
                        </span>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
